import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle, CheckCircle2, ChevronRight, Clock, MapPin, Info, XCircle, RefreshCw,
} from 'lucide-react';
import {
  billingApi,
  type BillingDriftDetail, type BillingDriftReport, type BillingDriftTenant,
  type DriftLocation, type DriftPackageDisplay, type DriftReconciledRow, type DriftRowVerdict,
} from '../../services/billingApi';
import { getIcon } from '../../services/iconRegistry';

import { RubyGemMark } from '../RubyGemMark';
/**
 * ENTITLEMENT-vs-BILLING DRIFT — raised for a human, never auto-corrected.
 *
 * Super Admin's package assignment (`POST /admin/tenants/{id}/packages`) writes
 * `tenant.enabledPackages` and makes no billing call, so a package granted
 * there is used immediately and charged for never. billing-service sweeps for
 * that nightly; this panel is where the answer surfaces.
 *
 * The ruling is deliberate: a background job must not start a charge on a real
 * card. It reports, an operator decides.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS PANEL OWES THE READER
 * ─────────────────────────────────────────────────────────────────────────────
 * It used to print raw `pkg_…` ids and nothing else, which is unactionable: an
 * operator cannot tell what a package IS, what it costs, or which side of the
 * ledger it sits on. Every row now carries the package's name, its icon and
 * colour from the packages table, and its monthly price; opening a tenant reads
 * Stripe LIVE and shows the three answers together — what the merchant can use,
 * what the signed plan says, and what Stripe is actually charging.
 */

const money = (cents?: number): string => {
  if (typeof cents !== 'number') return '—';
  return `$${(cents / 100).toFixed(2)}`;
};

const ageOf = (scannedAt: number | null): string => {
  if (!scannedAt) return 'never';
  const hours = Math.floor((Date.now() - scannedAt) / 3_600_000);
  if (hours < 1) return 'less than an hour ago';
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * Fall back to the raw id lists when a report predates the display join, so a
 * stale sweep still renders rather than showing an empty tenant.
 */
const displayFor = (f: BillingDriftTenant, which: 'entitledNotBilled' | 'billedNotEntitled'): DriftPackageDisplay[] => (
  f.display?.[which] ?? f[which].map((e) => ({ packageId: e.packageId, skuId: e.skuId, name: e.packageId }))
);

// ---------------------------------------------------------------------------
// Package identity — the icon tile, shared by every row on the panel.
// ---------------------------------------------------------------------------

/**
 * Most packages have never had an icon set (13 of 17 in dev), so the fallback
 * is not an edge case — it is the common path. Initials on the package's own
 * colour keep the column scannable without inventing an icon that would imply
 * something about what the package does.
 */
const initialsOf = (name: string): string => name
  .replace(/[^A-Za-z0-9 ]/g, ' ')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((w) => w[0]!.toUpperCase())
  .join('') || '?';

const PackageTile: React.FC<{ pkg: DriftPackageDisplay; size?: number }> = ({ pkg, size = 26 }) => {
  const bg = pkg.color || '#78716c';
  const Icon = pkg.icon ? getIcon(pkg.icon) : null;
  return (
    <span
      className="shrink-0 grid place-items-center rounded-lg text-white font-bold"
      style={{ width: size, height: size, background: bg, fontSize: size * 0.42 }}
      aria-hidden="true"
    >
      {Icon ? <Icon size={Math.round(size * 0.55)} strokeWidth={2.2} /> : initialsOf(pkg.name)}
    </span>
  );
};

const PackageName: React.FC<{ pkg: DriftPackageDisplay; showSku?: boolean }> = ({ pkg, showSku }) => (
  <div className="flex items-center gap-2.5 min-w-0">
    <PackageTile pkg={pkg} />
    <span className="min-w-0">
      <span className="block text-[13.5px] font-medium text-stone-900 truncate">{pkg.name}</span>
      {showSku && (
        <span className="block font-mono text-[10px] leading-tight text-stone-400 truncate">
          {pkg.skuId || pkg.packageId}
        </span>
      )}
    </span>
  </div>
);

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

const BillingDriftPanel: React.FC = () => {
  const [report, setReport] = useState<BillingDriftReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    billingApi.getBillingDrift()
      .then((r) => { if (!cancelled) setReport(r); })
      .catch((e: any) => { if (!cancelled) setError(e?.message || 'could not read the drift report'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-stone-200 bg-white">
        <RubyGemMark size={16} drawing />
        <p className="text-sm text-stone-500">Checking entitlement against billing…</p>
      </div>
    );
  }

  // A failed read is NOT "all clear". Say so, rather than rendering an empty
  // panel that looks identical to a clean estate.
  if (error || !report) {
    return (
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-red-200 bg-red-50">
        <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
        <p className="text-sm text-red-700">
          Entitlement-vs-billing check could not be read — {error}. This is not a clean result.
        </p>
      </div>
    );
  }

  if (report.neverRun) {
    return (
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-amber-200 bg-amber-50">
        <Clock className="w-4 h-4 text-amber-600 shrink-0" />
        <p className="text-sm text-amber-800">
          The entitlement-vs-billing sweep has never run, so nothing is known yet — not
          that nothing is wrong. It runs nightly.
        </p>
      </div>
    );
  }

  const leaking = report.findings.filter((f) => f.entitledNotBilled.length > 0);
  const overbilled = report.findings.filter((f) => f.billedNotEntitled.length > 0);
  const catalogGaps = report.findings.filter((f) => f.notInCatalog.length > 0);
  const neverPriced = report.neverPriced || [];
  const cleanupNames = report.namesNeedingCleanup || [];

  const nothingWrong = !leaking.length && !overbilled.length && !catalogGaps.length && !neverPriced.length;

  if (nothingWrong && !report.unreadable.length) {
    return (
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-emerald-200 bg-emerald-50">
        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
        <p className="text-sm text-emerald-800">
          Every tenant is billed for what they can use. Checked {ageOf(report.scannedAt)} across{' '}
          {plural(report.tenantsScanned, 'tenant')}.
        </p>
      </div>
    );
  }

  const unbilledTotal = report.totals?.unbilledMonthlyCents ?? 0;
  const overbilledTotal = report.totals?.overbilledMonthlyCents ?? 0;

  return (
    <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-stone-200 px-4 py-3 bg-stone-50">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
          <h3 className="font-semibold text-gray-900">Entitlement vs billing</h3>
        </div>
        <span className="text-xs text-stone-500 tabular-nums">
          checked {ageOf(report.scannedAt)} · {plural(report.tenantsScanned, 'tenant')}
        </span>
      </div>

      {/* The money headline. Only shown when there IS money in it — a zero
          total rendered in a warning band reads as an alarm about nothing. */}
      {(unbilledTotal > 0 || overbilledTotal > 0) && (
        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 px-4 py-3 bg-amber-50 border-b border-amber-200">
          {unbilledTotal > 0 && (
            <span className="flex items-baseline gap-2">
              <span className="font-mono text-xl font-bold text-amber-700 tabular-nums">{money(unbilledTotal)}/mo</span>
              <span className="text-[13px] text-stone-600">enabled for merchants and billed by nothing</span>
            </span>
          )}
          {overbilledTotal > 0 && (
            <span className="flex items-baseline gap-2">
              <span className="font-mono text-xl font-bold text-red-700 tabular-nums">{money(overbilledTotal)}/mo</span>
              {/* NOT "being charged". This sweep makes no Stripe call, so it
                  knows what the PLAN prices and nothing about what the card is
                  actually billed — a tenant priced for a package with no
                  subscription at all is charged zero. Open the tenant to find
                  out; only the drill-down reads Stripe. */}
              <span className="text-[13px] text-stone-600">priced on plans for packages nobody can open</span>
            </span>
          )}
        </div>
      )}

      <div className="divide-y divide-stone-100">
        {overbilled.length > 0 && (
          <Section
            tone="red"
            title={`Priced but not enabled · ${plural(overbilled.length, 'tenant')}`}
            note="Their plan prices a package the merchant cannot open. Whether Stripe is actually taking the money is a separate question — this sweep never calls Stripe. Open a tenant to see the live subscription."
          >
            {overbilled.map((f) => (
              <TenantRow
                key={f.tenantId}
                finding={f}
                packages={displayFor(f, 'billedNotEntitled')}
                countLabel={`${f.billedNotEntitled.length} priced, not enabled`}
                amountCents={f.overbilledMonthlyCents}
                tone="red"
              />
            ))}
          </Section>
        )}

        {leaking.length > 0 && (
          <Section
            tone="amber"
            title={`Used but never charged · ${plural(leaking.length, 'tenant')}`}
            note="Granting a package in Super Admin changes what a merchant can use, not what they pay. Publish a plan change to start billing it."
          >
            {leaking.map((f) => (
              <TenantRow
                key={f.tenantId}
                finding={f}
                packages={displayFor(f, 'entitledNotBilled')}
                countLabel={`${f.entitledNotBilled.length} unbilled`}
                amountCents={f.unbilledMonthlyCents}
                tone="amber"
              />
            ))}
          </Section>
        )}

        {catalogGaps.length > 0 && (
          <Section
            tone="stone"
            title={`No catalog SKU · ${plural(catalogGaps.length, 'tenant')}`}
            note="Not a billing fault — nobody could price these. The catalog needs a SKU before they can be billed at all."
          >
            {catalogGaps.map((f) => (
              <TenantRow
                key={f.tenantId}
                finding={f}
                packages={f.display?.notInCatalog ?? f.notInCatalog.map((p) => ({ packageId: p, name: p }))}
                countLabel={`${f.notInCatalog.length} unpriceable`}
                tone="stone"
              />
            ))}
          </Section>
        )}

        {neverPriced.length > 0 && (
          <Section
            tone="stone"
            title={`Packages, but nobody ever priced them · ${plural(neverPriced.length, 'tenant')}`}
            note="Never priced at all rather than priced wrongly — they need a plan assignment before billing can follow them. Kept apart so they do not bury the tenants above."
          >
            {neverPriced.map((t) => (
              <NeverPricedRow key={t.tenantId} tenant={t} />
            ))}
          </Section>
        )}

        {report.unreadable.length > 0 && (
          <Section
            tone="red"
            title={`Could not be checked · ${plural(report.unreadable.length, 'tenant')}`}
            note="These were skipped, so the result above is a floor and not the whole platform."
          >
            {report.unreadable.map((u) => (
              <div key={u.tenantId} className="flex flex-wrap items-baseline justify-between gap-2 px-3 py-2 text-sm">
                <span className="font-mono text-xs text-stone-700">{u.tenantId}</span>
                <span className="text-stone-500 text-xs">{u.reason}</span>
              </div>
            ))}
          </Section>
        )}
      </div>

      {cleanupNames.length > 0 && (
        <div className="flex gap-2.5 px-4 py-3 bg-stone-50 border-t border-stone-200">
          <Info className="w-3.5 h-3.5 text-stone-400 shrink-0 mt-0.5" />
          <p className="text-xs text-stone-600">
            {plural(cleanupNames.length, 'package name')} in the catalog carry stray whitespace and are trimmed
            for display here rather than hidden:{' '}
            {cleanupNames.map((n) => n.name).join(', ')}. Worth fixing at the source.
          </p>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------

const TONES = {
  amber: { text: 'text-amber-700', dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-700 border-amber-200', amt: 'text-amber-700' },
  red: { text: 'text-red-700', dot: 'bg-red-500', chip: 'bg-red-50 text-red-700 border-red-200', amt: 'text-red-700' },
  stone: { text: 'text-stone-600', dot: 'bg-stone-400', chip: 'bg-stone-100 text-stone-600 border-stone-200', amt: 'text-stone-600' },
} as const;

type Tone = keyof typeof TONES;

const Section: React.FC<{ tone: Tone; title: string; note: string; children: React.ReactNode }> = ({
  tone, title, note, children,
}) => (
  <div className="px-2 py-3">
    <div className="px-2">
      <p className={`flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider ${TONES[tone].text}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${TONES[tone].dot}`} />
        {title}
      </p>
      <p className="text-xs text-stone-500 mt-1 max-w-[76ch]">{note}</p>
    </div>
    <div className="mt-1.5">{children}</div>
  </div>
);

// ---------------------------------------------------------------------------

const TenantRow: React.FC<{
  finding: BillingDriftTenant;
  packages: DriftPackageDisplay[];
  countLabel: string;
  amountCents?: number;
  tone: Tone;
}> = ({ finding, packages, countLabel, amountCents, tone }) => {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<BillingDriftDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    setDetailError(null);
    try {
      setDetail(await billingApi.getBillingDriftDetail(finding.tenantId));
    } catch (e: any) {
      setDetailError(e?.message || 'could not read this tenant');
    } finally {
      setBusy(false);
    }
  }, [finding.tenantId]);

  // Stripe is read only when a human opens the tenant — never as part of
  // rendering the list, which would make loading the command center cost one
  // Stripe call per drifting tenant.
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !detail && !busy) void load();
  };

  return (
    <div className="border-t border-stone-100 first:border-t-0">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-stone-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
      >
        <ChevronRight className={`w-3.5 h-3.5 shrink-0 text-stone-400 transition-transform ${open ? 'rotate-90' : ''}`} />
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-semibold text-stone-900 truncate">
            {finding.businessName || <span className="font-mono text-xs font-medium">{finding.tenantId}</span>}
          </span>
          {finding.businessName && (
            <span className="block font-mono text-[10.5px] text-stone-400 truncate">{finding.tenantId}</span>
          )}
        </span>
        <span className="flex items-center gap-2.5 shrink-0">
          <span className={`text-[11.5px] font-semibold px-2 py-0.5 rounded-full border ${TONES[tone].chip}`}>
            {countLabel}
          </span>
          {typeof amountCents === 'number' && amountCents > 0 && (
            <span className={`font-mono text-[13px] font-bold tabular-nums ${TONES[tone].amt}`}>
              {money(amountCents)}/mo
            </span>
          )}
        </span>
      </button>

      {open && (
        <div className="pl-9 pr-3 pb-4 space-y-3">
          {/* The sweep's own answer, always available and never blocked on
              Stripe — so the row says something useful even if Stripe is down. */}
          <div className="flex flex-wrap gap-1.5">
            {packages.map((p) => (
              <span
                key={p.packageId}
                className="inline-flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full border border-stone-200 bg-white"
              >
                <PackageTile pkg={p} size={18} />
                <span className="text-[12.5px] font-medium text-stone-800">{p.name}</span>
                {typeof p.amountCents === 'number' && (
                  <span className="font-mono text-[11px] tabular-nums text-stone-500">{money(p.amountCents)}</span>
                )}
              </span>
            ))}
          </div>

          {busy && (
            <p className="flex items-center gap-2 text-xs text-stone-500">
              <RubyGemMark size={14} drawing color="currentColor" /> Reading this tenant’s live Stripe subscriptions…
            </p>
          )}

          {detailError && (
            <p className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              Live comparison unavailable — {detailError}. The packages above still come from the nightly sweep.
            </p>
          )}

          {detail && <TenantDetail detail={detail} onReload={load} busy={busy} />}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------

const TenantDetail: React.FC<{ detail: BillingDriftDetail; onReload: () => void; busy: boolean }> = ({
  detail, onReload, busy,
}) => {
  if (detail.noLocations) {
    return (
      <div className="flex items-start gap-2 text-xs text-stone-600 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2">
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-stone-400" />
        This tenant has no active location, so there is no Stripe subscription to compare against. The packages
        above are enabled and cannot be billed anywhere until a location exists.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {detail.locations.map((loc) => <LocationTable key={loc.locationId} loc={loc} />)}

      <div className="flex items-center gap-3 text-[11px] text-stone-400">
        <button
          type="button"
          onClick={onReload}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-stone-200 text-stone-600 hover:bg-stone-50 disabled:opacity-50"
        >
          {busy ? <RubyGemMark size={12} drawing color="currentColor" /> : <RefreshCw className="w-3 h-3" />} Re-read Stripe
        </button>
        <span>
          Stripe read {new Date(detail.readAt).toLocaleTimeString()}
          {detail.assignment
            ? ` · plan ${detail.assignment.assignmentId} (${detail.assignment.status})`
            : ' · no active plan assignment'}
        </span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------

const VERDICTS: Record<DriftRowVerdict, { row: string; note?: string }> = {
  aligned: { row: '' },
  unbilled: { row: 'bg-amber-50/70', note: 'enabled, on no plan and on no Stripe line' },
  overbilled: { row: 'bg-red-50/70', note: 'Stripe is charging for this and the merchant cannot open it' },
  planned_not_charged: { row: 'bg-amber-50/40', note: 'on the plan, but Stripe has no line for it' },
  charged_off_plan: { row: 'bg-amber-50/40', note: 'Stripe is charging for this and no plan agreed it' },
};

const Mark: React.FC<{ on: boolean }> = ({ on }) => (
  on
    ? <span className="text-emerald-600 font-bold" aria-label="yes">✓</span>
    : <span className="text-stone-300" aria-label="no">—</span>
);

const LocationTable: React.FC<{ loc: DriftLocation }> = ({ loc }) => (
  <div className="border border-stone-200 rounded-xl overflow-hidden bg-white">
    <div className="flex flex-wrap items-center justify-between gap-3 px-3.5 py-2.5 bg-stone-50 border-b border-stone-200">
      <span className="flex items-center gap-2 text-[13px] font-semibold text-stone-800">
        <MapPin className="w-3.5 h-3.5 text-stone-400" />
        {loc.name || loc.locationId}
        {loc.subscriptionId && (
          <span className="font-mono text-[10.5px] font-normal text-stone-400">{loc.subscriptionId}</span>
        )}
      </span>
      {loc.stripeUnavailable
        ? <span className="text-[11.5px] text-stone-500 italic">{loc.stripeUnavailable}</span>
        : (
          <span className="text-xs text-stone-500">
            Stripe charges{' '}
            <b className="font-mono text-[13px] text-stone-900 tabular-nums">{money(loc.stripeMonthlyCents)}/mo</b>
          </span>
        )}
    </div>

    {loc.rows.length === 0 ? (
      <p className="px-3.5 py-3 text-xs text-stone-500">
        No packages are enabled, planned or charged at this location.
      </p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] border-collapse">
          <thead>
            <tr className="text-[10px] font-bold uppercase tracking-wider text-stone-400">
              <th className="text-left px-3.5 py-2 border-b border-stone-200">Package</th>
              <th className="px-2 py-2 border-b border-stone-200">Enabled</th>
              <th className="px-2 py-2 border-b border-stone-200">On plan</th>
              <th className="px-2 py-2 border-b border-stone-200">On Stripe</th>
              <th className="text-right px-3.5 py-2 border-b border-stone-200">Monthly</th>
            </tr>
          </thead>
          <tbody>
            {loc.rows.map((r) => <Row key={r.packageId} r={r} />)}
          </tbody>
        </table>
      </div>
    )}

    {loc.otherLines.length > 0 && (
      <div className="px-3.5 py-2.5 border-t border-stone-200">
        <p className="text-[10px] font-bold uppercase tracking-wider text-stone-400 mb-1">
          Also on this subscription — not package entitlements
        </p>
        {loc.otherLines.map((l) => (
          <div key={l.itemId} className="flex justify-between gap-3 py-0.5 text-xs text-stone-600">
            <span className="font-mono text-[11px] truncate">{l.skuId || l.priceId}</span>
            <span className="font-mono tabular-nums shrink-0">
              {money(l.amountCents)}{l.quantity > 1 ? ` × ${l.quantity}` : ''}
            </span>
          </div>
        ))}
      </div>
    )}

    <div className="px-3.5 py-2.5 bg-stone-50 border-t border-stone-200 space-y-1">
      {loc.overbilledMonthlyCents > 0 && (
        <p className="flex items-center gap-2 text-[12.5px] text-red-700">
          <XCircle className="w-3.5 h-3.5 shrink-0" />
          <span>
            <b className="font-mono tabular-nums">{money(loc.overbilledMonthlyCents)}/mo</b> is being taken for
            packages the merchant cannot open.
          </span>
        </p>
      )}
      {loc.unbilledMonthlyCents > 0 && (
        <p className="flex items-center gap-2 text-[12.5px] text-amber-700">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          <span>
            <b className="font-mono tabular-nums">{money(loc.unbilledMonthlyCents)}/mo</b> is enabled and
            collected by nothing.
          </span>
        </p>
      )}
      {loc.overbilledMonthlyCents === 0 && loc.unbilledMonthlyCents === 0 && !loc.stripeUnavailable && (
        <p className="flex items-center gap-2 text-[12.5px] text-emerald-700">
          <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
          Entitlement, plan and Stripe agree at this location.
        </p>
      )}
      {!loc.placementRecorded && (
        <p className="flex items-center gap-2 text-[12px] text-stone-500">
          <Info className="w-3.5 h-3.5 shrink-0 text-stone-400" />
          This assignment recorded no per-location placement, so the whole plan is shown at every location.
        </p>
      )}
    </div>
  </div>
);

const Row: React.FC<{ r: DriftReconciledRow }> = ({ r }) => {
  const v = VERDICTS[r.verdict];
  return (
    <tr className={`border-b border-stone-100 last:border-b-0 ${v.row}`}>
      <td className="px-3.5 py-2 align-middle">
        <PackageName pkg={r} showSku />
        {v.note && <span className="block text-[11px] text-stone-500 mt-0.5 pl-[34px]">{v.note}</span>}
      </td>
      <td className="px-2 py-2 text-center"><Mark on={r.enabled} /></td>
      <td className="px-2 py-2 text-center"><Mark on={r.onPlan} /></td>
      <td className="px-2 py-2 text-center"><Mark on={r.onStripe} /></td>
      <td className="px-3.5 py-2 text-right whitespace-nowrap">
        <span
          className={`font-mono text-[13px] tabular-nums ${
            r.verdict === 'overbilled' ? 'text-red-700 font-bold'
              : r.verdict === 'unbilled' ? 'text-amber-700 font-bold' : 'text-stone-700'
          }`}
        >
          {money(r.stripeAmountCents ?? r.amountCents)}
        </span>
        {r.pricedDifferently && (
          <span className="block text-[10.5px] text-stone-400">
            list {money(r.amountCents)}
          </span>
        )}
      </td>
    </tr>
  );
};

// ---------------------------------------------------------------------------

const NeverPricedRow: React.FC<{ tenant: BillingDriftReport['neverPriced'][number] }> = ({ tenant }) => {
  const [open, setOpen] = useState(false);
  const packages = tenant.display || [];
  const named = Boolean(tenant.businessName && tenant.businessName.trim());

  return (
    <div className="border-t border-stone-100 first:border-t-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        disabled={!packages.length}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-stone-50 disabled:hover:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
      >
        <ChevronRight
          className={`w-3.5 h-3.5 shrink-0 text-stone-400 transition-transform ${open ? 'rotate-90' : ''} ${packages.length ? '' : 'invisible'}`}
        />
        <span className="flex-1 min-w-0">
          {named
            ? <>
                <span className="block text-sm font-semibold text-stone-900 truncate">{tenant.businessName}</span>
                <span className="block font-mono text-[10.5px] text-stone-400 truncate">{tenant.tenantId}</span>
              </>
            : <>
                <span className="block font-mono text-xs text-stone-700 truncate">{tenant.tenantId}</span>
                {/* Flagged rather than disguised: an id rendered where a name
                    belongs looks like a naming style, not a missing record. */}
                <span className="inline-flex items-center gap-1 mt-1 text-[11px] font-semibold text-red-700 bg-red-50 rounded-full px-2 py-0.5">
                  <AlertTriangle className="w-3 h-3" /> no business name set
                </span>
              </>}
        </span>
        <span className="flex items-center gap-2.5 shrink-0">
          <span className="text-[11.5px] font-semibold px-2 py-0.5 rounded-full border bg-stone-100 text-stone-600 border-stone-200">
            {plural(tenant.enabledPackages, 'package')}
          </span>
          {typeof tenant.unpricedMonthlyCents === 'number' && tenant.unpricedMonthlyCents > 0 && (
            <span className="font-mono text-[13px] font-bold tabular-nums text-stone-500">
              {money(tenant.unpricedMonthlyCents)}/mo
            </span>
          )}
        </span>
      </button>

      {open && packages.length > 0 && (
        <div className="pl-9 pr-3 pb-3 flex flex-wrap gap-1.5">
          {packages.map((p) => (
            <span
              key={p.packageId}
              className="inline-flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full border border-stone-200 bg-white"
            >
              <PackageTile pkg={p} size={18} />
              <span className="text-[12.5px] font-medium text-stone-800">{p.name}</span>
              {typeof p.amountCents === 'number' && (
                <span className="font-mono text-[11px] tabular-nums text-stone-500">{money(p.amountCents)}</span>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default BillingDriftPanel;
